import { Box, Chip, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CopyableText from 'components/atoms/CopyableText';
import { isEmpty, map } from 'es-toolkit/compat';
import { Slide } from 'interfaces/slide';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import React from 'react';
import { useCurrentProcedurePageCardsPerRow } from '../../useProcedurePageColumns';
import { getSlideMetadataChipItems } from './getSlideMetadataChipItems';
import { getSlideCarouselTone, getSlideOverlayToneStyles } from './slideCarouselChrome';

const SlideMetadataChipContainer = styled('div')({
  position: 'absolute',
  top: '7px',
  right: '6px',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '4px',
  maxWidth: 'calc(100% - 12px)',
});

interface SlideMetadataChipCollectionProps {
  slide: Slide;
}

export const getVisibleSlideMetadataChipItems = ({
  chipItems,
  cardsPerRow,
}: {
  chipItems: ReturnType<typeof getSlideMetadataChipItems>;
  cardsPerRow: number;
}) => (cardsPerRow >= 5 ? chipItems.slice(0, 2) : chipItems);

const denseSlideMetadataLabelSx = {
  display: '-webkit-box',
  fontSize: 'clamp(10px, 0.78vw, 11px)',
  lineHeight: 1.2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  textAlign: 'right',
  color: 'inherit',
};

const SlideMetadataChipCollection: React.FC<SlideMetadataChipCollectionProps> = ({ slide }) => {
  const theme = useTheme();
  const currentCardsPerRow = useCurrentProcedurePageCardsPerRow();
  const slideTone = getSlideCarouselTone(slide.stainingType);
  const chipItems = getVisibleSlideMetadataChipItems({
    chipItems: getSlideMetadataChipItems(slide),
    cardsPerRow: currentCardsPerRow,
  });
  const useCompactOverlayMetadata = currentCardsPerRow >= 5;

  if (isEmpty(chipItems)) {
    return null;
  }

  const chipToneStyle = getSlideOverlayToneStyles(theme, slideTone);

  return (
    <SlideMetadataChipContainer>
      {map(chipItems, (chipItem) => (
        <Chip
          key={`${slide.id}-${chipItem.key}`}
          size="small"
          label={
            chipItem.textForCopy ? (
              <CopyableText
                textForCopy={chipItem.textForCopy}
                variant="caption"
                component="span"
                whiteSpace="normal"
                overflow="visible"
                textOverflow="clip"
                sx={{
                  cursor: 'pointer',
                  ...denseSlideMetadataLabelSx,
                  ...(useCompactOverlayMetadata
                    ? {
                        fontSize: '10px',
                        WebkitLineClamp: 1,
                      }
                    : undefined),
                }}
              >
                {chipItem.label}
              </CopyableText>
            ) : (
              <Box
                component="span"
                sx={{
                  ...denseSlideMetadataLabelSx,
                  ...(useCompactOverlayMetadata
                    ? {
                        fontSize: '10px',
                        WebkitLineClamp: 1,
                      }
                    : undefined),
                }}
              >
                {chipItem.label}
              </Box>
            )
          }
          style={chipToneStyle}
          sx={{
            width: 'fit-content',
            maxWidth: useCompactOverlayMetadata ? 'min(100%, 170px)' : 'min(100%, 220px)',
            height: 'auto',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            '& .MuiChip-label': {
              padding: useCompactOverlayMetadata ? '3px 6px' : '4px 8px',
              display: 'block',
            },
          }}
        />
      ))}
    </SlideMetadataChipContainer>
  );
};

export default SlideMetadataChipCollection;
